import { Duration } from 'luxon'
import LoanData = App.Data.EstateObject.LoanData

export type EstateObjectLoanCalculations = {
  totalLoanAmount: number | null
  totalMonthlyAnnuity: number | null
  loanCount: number
  avgInterestRate: number | null
  loanRate: number | null
  fixedInterestEndYear: number | null
  minLoanTermInYears: number | null
}

export function estateObjectLoanCalculations(loans: LoanData[] | null | undefined): EstateObjectLoanCalculations {
  if (!loans?.length) {
    return {
      totalLoanAmount: null,
      totalMonthlyAnnuity: null,
      loanCount: 0,
      avgInterestRate: null,
      loanRate: null,
      fixedInterestEndYear: null,
      minLoanTermInYears: null,
    }
  }

  const totalLoanAmount = loans.reduce((sum, loan) => sum + loan.principal_amount, 0)
  const loanCount = loans.length
  const avgInterestRate = loans.reduce((sum, loan) => sum + (loan.nominal_interest_rate || 0), 0) / loanCount
  const avgRepayment = loans.reduce((sum, loan) => sum + (loan.initial_repayment_rate || 0), 0) / loanCount
  const loanRate = avgInterestRate + avgRepayment

  return {
    totalLoanAmount,
    totalMonthlyAnnuity: totalMonthlyAnnuity(loans),
    loanCount,
    avgInterestRate,
    loanRate,
    fixedInterestEndYear: fixedInterestEndYear(loans),
    minLoanTermInYears: minLoanTermInYears(loans),
  }
}

function totalMonthlyAnnuity(loans: LoanData[]) {
  return loans.reduce((sum, loan) => sum + loan.installment_amount, 0)
}

function fixedInterestEndYear(loans: LoanData[]): number {
  const loansDurationsInYears = loans.map((loan) => Duration.fromISO(loan.fixed_interest_rate_term).years)
  const minLoanDurationInYears = Math.min(...loansDurationsInYears)

  return new Date().getFullYear() + minLoanDurationInYears
}

function minLoanTermInYears(loans: LoanData[]): number {
  const loanTermsInYears = loans.map((loan) => Duration.fromISO(loan.loan_term).years)

  return Math.min(...loanTermsInYears)
}

export function calculateRentalYieldInPercentage(
  loans: LoanData[] | null | undefined,
  purchasePrice: number | null | undefined,
) {
  if (!loans?.length || !purchasePrice) {
    return null
  }

  const totalAnnuityMonthly = totalMonthlyAnnuity(loans)
  const annualAnnuity = totalAnnuityMonthly * 12

  return (annualAnnuity / purchasePrice) * 100
}
